import React, { useState, useEffect } from 'react';
import { Phone, X, Loader2, Mic, Sparkles, UserPlus, CheckCircle, Bot } from 'lucide-react';
import { apiCall } from '../../services/api';

const CallDetailModal = ({ call, isOpen, onClose, onOutcomeUpdate, onLeadCapture }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [transcript, setTranscript] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(call?.outcome || null);
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [capturingLead, setCapturingLead] = useState(false);
  const [pipelineStage, setPipelineStage] = useState('new_lead');
  
  const outcomeOptions = [
    { id: 'booked', label: 'Booked', color: 'bg-green-500', icon: '📅' },
    { id: 'follow_up', label: 'Follow-up', color: 'bg-blue-500', icon: '🔄' },
    { id: 'hot_lead', label: 'Hot Lead', color: 'bg-orange-500', icon: '🔥' },
    { id: 'qualified', label: 'Qualified', color: 'bg-purple-500', icon: '✓' },
    { id: 'not_interested', label: 'Not Interested', color: 'bg-gray-500', icon: '✗' },
    { id: 'voicemail', label: 'Voicemail', color: 'bg-yellow-500', icon: '📧' },
  ];
  
  const pipelineStages = [
    { id: 'new_lead', label: 'New Lead' },
    { id: 'contacted', label: 'Contacted' },
    { id: 'qualified', label: 'Qualified' },
    { id: 'proposal', label: 'Proposal' },
    { id: 'negotiation', label: 'Negotiation' },
    { id: 'closed', label: 'Closed' },
  ];

  useEffect(() => {
    if (isOpen && call?.call_sid) {
      fetchTranscript();
      fetchExistingSummary();
    }
  }, [isOpen, call?.call_sid]);

  const fetchTranscript = async () => {
    if (!call?.call_sid) return;
    setLoadingTranscript(true);
    try {
      const data = await apiCall(`/calls/${call.call_sid}/transcript`);
      setTranscript(data);
    } catch (error) {
      console.error('Failed to fetch transcript:', error);
    } finally {
      setLoadingTranscript(false);
    }
  };

  const fetchExistingSummary = async () => {
    if (!call?.call_sid) return;
    try {
      const callData = await apiCall(`/calls/${call.call_sid}`);
      if (callData?.ai_summary) {
        setSummary({
          summary: callData.ai_summary,
          action_items: callData.action_items || [],
          sentiment: callData.sentiment,
          lead_quality: callData.lead_quality
        });
      }
    } catch (error) {
      console.log('No existing summary found');
    }
  };

  const transcribeCall = async () => {
    if (!call?.call_sid) return;
    setLoadingTranscript(true);
    try {
      const data = await apiCall(`/calls/${call.call_sid}/transcribe`, { method: 'POST' });
      setTranscript(prev => ({ ...prev, transcript: data.transcript, has_transcript: true }));
    } catch (error) {
      console.error('Failed to transcribe call:', error);
      alert('Failed to transcribe: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingTranscript(false);
    }
  };

  const generateSummary = async () => {
    if (!call?.call_sid) return;
    setLoadingSummary(true);
    try {
      const data = await apiCall(`/calls/${call.call_sid}/summary`, { method: 'POST' });
      setSummary(data);
    } catch (error) {
      console.error('Failed to generate summary:', error);
      alert('Failed to generate summary: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSaveOutcome = async () => {
    if (!selectedOutcome || !call?.call_sid) return;
    setSavingOutcome(true);
    try {
      await apiCall(`/calls/${call.call_sid}/outcome`, {
        method: 'PUT',
        body: JSON.stringify({
          outcome: selectedOutcome,
          notes: outcomeNotes,
          follow_up_date: followUpDate || null
        })
      });
      onOutcomeUpdate?.(call.call_sid, selectedOutcome);
      onClose();
    } catch (error) {
      console.error('Failed to save outcome:', error);
    } finally {
      setSavingOutcome(false);
    }
  };

  const handleCaptureLead = async () => {
    if (!call?.call_sid) return;
    setCapturingLead(true);
    try {
      const result = await apiCall(`/calls/${call.call_sid}/capture-lead`, {
        method: 'POST',
        body: JSON.stringify({
          call_sid: call.call_sid,
          add_to_pipeline: true,
          pipeline_stage: pipelineStage
        })
      });
      onLeadCapture?.(result);
      alert(`Lead captured successfully! ${result.is_new_contact ? 'New contact created.' : 'Existing contact updated.'}`);
    } catch (error) {
      console.error('Failed to capture lead:', error);
      alert('Failed to capture lead');
    } finally {
      setCapturingLead(false);
    }
  };

  if (!isOpen || !call) return null;

  const displayPhone = call.direction === 'inbound' ? call.from_phone : call.to_phone;
  const displayName = call.contact_name || displayPhone;
  const callDate = call.created_at ? new Date(call.created_at) : new Date();

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[700px] max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-400/20 bg-gradient-to-r from-gray-900 to-gray-800">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Phone size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{displayName}</h2>
                <p className="text-gray-400 text-sm">{displayPhone}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    call.direction === 'inbound' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {call.direction === 'inbound' ? '↓ Inbound' : '↑ Outbound'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {callDate.toLocaleDateString()} at {callDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </span>
                  {call.duration && (
                    <span className="text-xs text-gray-500">• {Math.floor(call.duration / 60)}m {call.duration % 60}s</span>
                  )}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-400/20">
          {['details', 'transcript', 'lead'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab 
                  ? 'text-white border-b-2 border-green-500' 
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab === 'details' && 'Call Outcome'}
              {tab === 'transcript' && 'Transcript & Summary'}
              {tab === 'lead' && 'Lead Capture'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Call Outcome Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 block">
                  Call Outcome
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {outcomeOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setSelectedOutcome(option.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        selectedOutcome === option.id
                          ? `${option.color} border-white text-white`
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      <span className="text-2xl block mb-1">{option.icon}</span>
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {selectedOutcome === 'follow_up' && (
                <div>
                  <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
                    Follow-up Date
                  </label>
                  <input
                    type="datetime-local"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-500"
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
                  Notes
                </label>
                <textarea
                  value={outcomeNotes}
                  onChange={(e) => setOutcomeNotes(e.target.value)}
                  placeholder="Add notes about this call..."
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 resize-none"
                />
              </div>

              <button
                onClick={handleSaveOutcome}
                disabled={!selectedOutcome || savingOutcome}
                className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                  selectedOutcome && !savingOutcome
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                {savingOutcome ? <Loader2 size={20} className="animate-spin" /> : 'Save Outcome'}
              </button>
            </div>
          )}

          {/* Transcript Tab */}
          {activeTab === 'transcript' && (
            <div className="space-y-6">
              {(call.vapi_handled || call.vapi_transcript) && (
                <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Bot size={20} className="text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Handled by AI Receptionist (Klara)</p>
                    <p className="text-gray-400 text-sm">This call was answered by your VAPI AI assistant</p>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                    Call Transcript
                  </label>
                  <div className="flex items-center gap-2">
                    {(transcript?.has_recording || call.recording_url || call.vapi_recording_url) && !transcript?.transcript && !call.vapi_transcript && (
                      <button
                        onClick={transcribeCall}
                        disabled={loadingTranscript}
                        className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        {loadingTranscript ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />}
                        Transcribe Recording
                      </button>
                    )}
                    {(transcript?.recording_url || call.recording_url || call.vapi_recording_url) && (
                      <a
                        href={transcript?.recording_url || call.vapi_recording_url || call.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white flex items-center gap-1"
                      >
                        <Mic size={12} /> Play Recording
                      </a>
                    )}
                  </div>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 min-h-[150px]">
                  {loadingTranscript ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader2 size={24} className="animate-spin text-green-500 mb-2" />
                      <p className="text-gray-400 text-sm">Transcribing with AI...</p>
                    </div>
                  ) : (transcript?.transcript || call.vapi_transcript) ? (
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                      {call.vapi_transcript || transcript.transcript}
                    </p>
                  ) : (
                    <p className="text-gray-500 text-sm italic text-center py-4">
                      {(transcript?.has_recording || call.recording_url || call.vapi_recording_url)
                        ? 'Click "Transcribe Recording" to generate transcript.'
                        : 'No recording available for this call.'}
                    </p>
                  )}
                </div>
              </div>

              {call.vapi_summary && (
                <div>
                  <label className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3 block">
                    AI Receptionist Summary
                  </label>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                      {call.vapi_summary}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                    AI Analysis & Summary
                  </label>
                  <button
                    onClick={generateSummary}
                    disabled={loadingSummary || (!transcript?.transcript && !call.vapi_transcript)}
                    className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${
                      (transcript?.transcript || call.vapi_transcript) && !loadingSummary
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {loadingSummary ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    Generate Summary
                  </button>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                  {loadingSummary ? (
                    <div className="flex flex-col items-center justify-center py-4">
                      <Loader2 size={24} className="animate-spin text-purple-500 mb-2" />
                      <p className="text-gray-400 text-sm">Analyzing call with AI...</p>
                    </div>
                  ) : summary?.summary ? (
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-purple-400 font-semibold text-sm mb-2">Summary</h4>
                        <p className="text-gray-300 text-sm leading-relaxed">{summary.summary}</p>
                      </div>
                      {summary.action_items?.length > 0 && (
                        <div>
                          <h4 className="text-purple-400 font-semibold text-sm mb-2">Action Items</h4>
                          <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                            {summary.action_items.map((item, idx) => <li key={idx}>{item}</li>)}
                          </ul>
                        </div>
                      )}
                      <div className="flex gap-4">
                        {summary.sentiment && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">Sentiment:</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              summary.sentiment === 'positive' ? 'bg-green-500/20 text-green-400' :
                              summary.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>{summary.sentiment}</span>
                          </div>
                        )}
                        {summary.lead_quality && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">Lead Quality:</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              summary.lead_quality === 'hot' ? 'bg-orange-500/20 text-orange-400' :
                              summary.lead_quality === 'warm' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>{summary.lead_quality}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm italic">
                      Click "Generate Summary" to create an AI-powered summary.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Lead Capture Tab */}
          {activeTab === 'lead' && (
            <div className="space-y-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <UserPlus size={18} className="text-blue-400" />
                  Capture Lead from Call
                </h3>
                <p className="text-gray-400 text-sm">
                  Convert this caller into a contact and add them to your sales pipeline.
                </p>
              </div>

              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">Caller Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Name:</span>
                    <span className="text-white">{displayName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Phone:</span>
                    <span className="text-white">{displayPhone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Call Date:</span>
                    <span className="text-white">{callDate.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 block">
                  Pipeline Stage
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {pipelineStages.map((stage) => (
                    <button
                      key={stage.id}
                      onClick={() => setPipelineStage(stage.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        pipelineStage === stage.id
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {stage.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleCaptureLead}
                disabled={capturingLead}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {capturingLead ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    <UserPlus size={20} />
                    Capture Lead & Add to Pipeline
                  </>
                )}
              </button>

              {call.lead_captured && (
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle size={18} className="text-green-400" />
                  <span className="text-green-400 text-sm">Lead already captured from this call</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallDetailModal;
